import {createContext, FC, useContext, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import qs from 'qs'
import {WithChildren} from '../../../../../_metronic/helpers'
import {
  FilterDates,
  MetricClientsResponse,
  MetricFilters,
  MetricProducts,
  MetricSummary,
  MetricTab,
} from './_models'
import {getMetricClients, getMetricProducts, getMetricSummary} from './_requests'

const PER_PAGE = 15

type MetricContextProps = {
  activeTab: MetricTab
  setActiveTab: (tab: MetricTab) => void
  filters: MetricFilters
  applyFilters: (filters: MetricFilters) => void
  filtersQuery: string
  setPage: (page: number) => void
  summary?: MetricSummary
  products?: MetricProducts
  clients?: MetricClientsResponse
  filterDates: FilterDates
  isLoading: boolean
}

const MetricContext = createContext<MetricContextProps>({} as MetricContextProps)

// Remove valores vazios para não enviar parâmetros em branco ao backend.
const stringifyFilters = (params: Record<string, unknown>): string => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && !value.length)
    )
  )

  return qs.stringify(cleaned, {arrayFormat: 'comma'})
}

const MetricProvider: FC<WithChildren> = ({children}) => {
  const [activeTab, setActiveTab] = useState<MetricTab>('summary')
  const [filters, setFilters] = useState<MetricFilters>({})
  const [page, setPage] = useState<number>(1)

  const filtersQuery = useMemo(() => stringifyFilters(filters), [filters])
  const clientsQuery = useMemo(
    () => stringifyFilters({...filters, page, per_page: PER_PAGE}),
    [filters, page]
  )

  // Resultado nunca fica obsoleto: ao voltar para uma aba, só consulta de novo se o filtro (chave) mudou.
  const queryOptions = {
    cacheTime: 0,
    staleTime: Infinity,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  }

  // Cada aba só consulta quando está ativa: as agregações são pesadas.
  const summaryQuery = useQuery(
    `metric-summary-${filtersQuery}`,
    () => getMetricSummary(filtersQuery),
    {...queryOptions, enabled: activeTab === 'summary'}
  )
  const productsQuery = useQuery(
    `metric-products-${filtersQuery}`,
    () => getMetricProducts(filtersQuery),
    {...queryOptions, enabled: activeTab === 'products'}
  )
  const clientsQueryResult = useQuery(
    `metric-clients-${clientsQuery}`,
    () => getMetricClients(clientsQuery),
    {...queryOptions, enabled: activeTab === 'clients'}
  )

  const activeQuery = {
    summary: summaryQuery,
    products: productsQuery,
    clients: clientsQueryResult,
  }[activeTab]

  const applyFilters = (newFilters: MetricFilters) => {
    setFilters(newFilters)
    setPage(1)
  }

  return (
    <MetricContext.Provider
      value={{
        activeTab,
        setActiveTab,
        filters,
        applyFilters,
        filtersQuery,
        setPage,
        summary: summaryQuery.data?.data,
        products: productsQuery.data?.data,
        clients: clientsQueryResult.data,
        filterDates: activeQuery.data?.filterDates ?? {},
        isLoading: activeQuery.isFetching,
      }}
    >
      {children}
    </MetricContext.Provider>
  )
}

const useMetric = () => useContext(MetricContext)

export {MetricProvider, useMetric}
